import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// depedencies
import CryptoES from 'crypto-es';
import * as tus from 'tus-js-client';
import { FileSizePipe} from 'ngx-filesize';

import * as FileObject from '@core/objects/file';
import { NotificationData } from '@core/store/models/notifications.interface';
import { Record } from '@core/store/models/recorder.class';
// core recorder state 
import * as fromState from '@core/store/state/recorder/recorder.state';

// services
import { RecorderService } from '@core/services/recorder/recorder.service';
import { UrlService } from '@core/services/url/url.service';

interface FileUploadInterface {
    file: File;
    status: number;
    progress: number;
    hash: string;
    object: FileObject.File;
}

@Component({
    selector: 'core-layout-modal-record-player',
    templateUrl: './modal-record-player.component.html',
    styleUrls: ['./modal-record-player.component.scss']
})
/**
 * @input header is the title of the modal, there is a default
 * @input text is the message under the title, there is a default
 * @input buttonText is the main white button text
 * @input leftButtonText is the left gray button text, 
 *        if empty, only one button shown
 * @input disabled: in case of we want to disable the main button from parent
 * 
 * @output action main button action emitter
 * @output leftButtonAction left button action emitter
 * 
 */
export class LayoutModalRecordPlayerComponent implements OnInit, OnDestroy {
    public readonly STATUS_MEDIA_LOADING = 1;
    public readonly STATUS_MEDIA_READY = 2;
    public readonly STATUS_MEDIA_ERROR = 3;
    public readonly STATUS_UPLOAD_ERROR = 4;
    // File status
    public readonly STATUS_FILE_INITIAL = 0;
    public readonly STATUS_FILE_PENDING = 1;
    public readonly STATUS_FILE_SUCCESS = 2;
    public readonly STATUS_FILE_ERROR = 3;

    private readonly VIDEO_FILE_MIME_TYPE = 'video/webm';

    @Input() text: string = '';
    @Input() header: string = '';
    @Input() buttonText: string;
    @Input() leftButtonText: string = "";
    @Input() disabled: boolean = false;

    @Output() action = new EventEmitter<boolean>();
    @Output() leftButtonAction = new EventEmitter<boolean>();

    public status: number = this.STATUS_MEDIA_LOADING;
    
    public videoFile: File;
    public videoSrc: any;
    // TUS
    protected uploaderTus:  Array<tus.Upload> = [];
    protected fileToUpload: FileUploadInterface;

    protected notificationData: NotificationData;
    protected recordingStatus: fromState.RecorderStatus;

    protected subs: Subscription;
    protected notDataSubs: Subscription;

    constructor(private recorderSrv: RecorderService,
                private sanitizer : DomSanitizer,
                private urlSrv: UrlService,
                private http: HttpClient){}

    ngOnInit(): void {
        // here we check the media
        this.subs = this.recorderSrv.getStatus().subscribe((status: fromState.RecorderStatus ) => {
            this.recordingStatus = status;
        });

        this.notDataSubs = this.recorderSrv.getNotificationData().subscribe((nData: NotificationData) => {
            // console.debug(" NotificationData TO SEND VIDEO  IS ", this.notificationData);
            this.notificationData = nData;
        });
        // listen the chunks of the file from the store
        this.recorderSrv.getStreamChunks().subscribe((chunks: Blob[]) => {
            if(chunks != undefined && this.recordingStatus == fromState.RecorderStatus.STOPPED){
                const newfile = this.newFile(chunks);
                // const newblob = new Blob(chunks,{type:"video/webm"}); //NOT USED NOW
                // you can use the file object to upload it to a server or play it in a <video> element
                // sanitizer lets you play locally to prevent a "unsafe" browser message "WARNING: sanitizing unsafe URL value blob:...."
                let link = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(newfile)); //using URL.revokeObjectURL once you no longer need it.
                console.debug(" Blob FILE URL RECORDED: ",link);
                this.videoSrc = link;
                this.status = this.STATUS_MEDIA_READY;
                // build the file from the blob data.
                this.createFileToUpload(newfile);
            }
        });

    }

    ngOnDestroy() {
        // need to be released 
        if(this.videoSrc)
            URL.revokeObjectURL(this.videoSrc);
        if(this.subs)
            this.subs.unsubscribe();
        if(this.uploaderTus)
            this.uploaderTus = [];
      }

    onAction() {
        this.createTusUploader();
        // disable save button until message is sent
        this.disabled = true;
    }
    // cancel
    onLeftAction() {
        this.leftButtonAction.emit(true);
    }
    
    // Create a video file using the videoBlob 
    protected newFile(blobChunks: Blob[]) : File {
        // here we can save it locally (it's in the RAM) and set the name
        const currentDate = new Date();
        const dateString = currentDate.toISOString().split('T')[0].replace(/-/g, '');
        const timeString = currentDate.toLocaleTimeString().replace(/:/g, '').replace(/ /g, '');
        const createdFile = new File(blobChunks, `video-message-${dateString}-${timeString}.webm`, { type: this.VIDEO_FILE_MIME_TYPE });
        // create the FileInterface before upload it
        this.videoFile = createdFile;
        this.createFileToUpload(createdFile);
        return createdFile;
    }

    protected createFileToUpload(file: File) {
        let i = CryptoES.MD5(file.name + file.size).toString();
        this.fileToUpload = {
            file: file,
            status: this.STATUS_FILE_INITIAL,
            progress: 0,
            hash: i.toString(),
            object: null
        };
    }

    protected setProgress(uploaded: number, total: number) {
        let p: number = +(uploaded / total * 100).toFixed(2)        
        this.fileToUpload.progress = p;
            
    }

    protected createTusUploader(){
        let uploader: tus.Upload = new tus.Upload(this.videoFile, {
            endpoint: this.urlSrv.build("/files"),
            chunkSize: 10000000,
            retryDelays: [0, 3000],
            metadata: {
                filename: this.videoFile.name,
                filetype: this.videoFile.type
            },
            // weird fix for sessions
            onBeforeRequest: (req) => {
                var xhr = req.getUnderlyingObject();
                xhr.withCredentials = true;
            }
        });

        uploader.options.onError = (error) => {
            console.log("Failed because: " + error, this.uploaderTus);
            this.fileToUpload.status = this.STATUS_FILE_ERROR;
            this.status = this.STATUS_UPLOAD_ERROR;
            // TODO sent a notification here
        };

        uploader.options.onProgress = (bytesUploaded, bytesTotal) => {
            this.setProgress( bytesUploaded, bytesTotal)
        };

        uploader.options.onSuccess = () => {
            // Change file to success in listFiles
            this.onUploadSuccess();
        }

        uploader.start();
        this.fileToUpload.status = this.STATUS_FILE_PENDING;

        this.uploaderTus[this.fileToUpload.hash] = uploader;
    }

    private onUploadSuccess(){
        this.http.get<any>(this.uploaderTus[this.fileToUpload.hash].url+"?f=json").subscribe(
            (data: FileObject.File) => {
                console.debug(" FILE OBJECT UPLOADED is: ",data)
                this.fileToUpload.object = data;
                this.fileToUpload.status = this.STATUS_FILE_SUCCESS;
                // Send file message here...
                this.submitFile(this.fileToUpload.object);
            },
            (error:any) => {
                console.debug(" FILE OBJECT UPLOAD error:", error);
                this.fileToUpload.status = this.STATUS_FILE_ERROR;
                // TODO sent a notification here
            }
        );
    }

    // stop the uploading process of the file
    public removeFile(file: FileUploadInterface) {
        if(this.fileToUpload.status !== this.STATUS_FILE_SUCCESS) {
            this.uploaderTus[this.fileToUpload.hash].abort();
            delete this.uploaderTus[this.fileToUpload.hash];
            this.disabled = false;
        }
    }

    // get the file name to show on the uploader with the size
    public getViewName() {
        let filesize = new FileSizePipe();
        return this.videoFile.name +" (" + filesize.transform(this.videoFile.size) + ")";
    }

    // prepare the file video message post values to send
    protected submitFile(file: FileObject.File){
        let value = {
            message: this.notificationData.details.user.name + ' left a recording for you.',
            files: [file.id],
            type: "video"
        }
        // send data to message
        this.send(value).subscribe({
            next: (data:any) => {
                console.log('Message saved: ',data);
                // TODO: sent notification success here
                // Set finish success in recording store to update message list
                this.recorderSrv.finish(true);
                this.action.emit(data);
            },
            error:(error:any) => {
                console.log('Error message: ',error);
                // TODO: sent notification failed here
                // Set finish fail in recording store to update message list
                this.recorderSrv.finish(false);
            }
        });
    }

    // send message to the action url saved on notification data
    private send(value:any) {
        let options = { headers: new HttpHeaders({
            'Content-Type':  'application/json',
        })};
        return this.http.post<string>(this.notificationData.details.action, value, options);
    }

}
